import React, { useState, useEffect, useRef } from "react";
import { MenuItem } from "@mui/material";
import DropdownIcon from "../../assets/svg/dropdown.svg";
import "./Statusdropdown.css";

type Status =
  | "Applied"
  | "Shortlisted"
  | "Screening"
  | "Interview"
  | "Offered"
  | "Hired"
  | "On Hold"
  | "Rejected"
  | "Non-Emirati"
  | string;

interface StatusOption {
  key: string;
  label: string;
  order: number;
}

interface StatusSelectProps {
  value: string;
  onChange: any;
  appId: string;
  statusOptions?: StatusOption[];
}

const statusStyles: { [key in Status]: { background: string; color: string } } =
  {
    Applied: {
      background: "rgba(43, 195, 227, 0.08)",
      color: "rgba(43, 195, 227, 1)",
    },
    Shortlisted: {
      background: "rgba(215, 234, 243, 1)",
      color: "rgba(12, 98, 143, 1)",
    },
    Screening: {
      background: "rgba(233, 242, 255, 1)",
      color: "rgba(44, 92, 167, 1)",
    },
    Interview: {
      background: "rgba(225, 238, 250, 1)",
      color: "rgba(48, 130, 217, 1)",
    },
    Offered: {
      background: "rgba(233, 249, 239, 1)",
      color: "rgba(34, 197, 94, 1)",
    },
    Hired: {
      background: "rgba(233, 249, 239, 1)",
      color: "rgba(34, 197, 94, 1)",
    },
    "On Hold": {
      background: "rgba(255, 244, 236, 1)",
      color: "rgba(238, 132, 52, 1)",
    },
    Rejected: {
      background: "rgba(255, 244, 244, 1)",
      color: "rgba(239, 68, 68, 1)",
    },
    "Non-Emirati": {
      background: "rgba(237, 237, 237, 1)",
      color: "rgba(129, 129, 129, 1)",
    },
  };

const StatusDropDown: React.FC<StatusSelectProps> = ({
  value,
  onChange,
  appId,
  statusOptions,
}) => {
  const [changedVal, setChangedVal] = useState(value);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Update internal state when value prop changes
  useEffect(() => {
    setChangedVal(value);
  }, [value]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleSelectChange = (selectedStatus: string) => {
    setChangedVal(selectedStatus);
    onChange(selectedStatus, appId);
    setIsOpen(false); // Close the dropdown after selecting an option
  };

  const selectedStyle =
    statusStyles[changedVal as Status] || statusStyles["Applied"];

  return (
    <div className="status-dropdown" ref={dropdownRef}>
      <div
        className={`status-dropdown__selected ${changedVal
          .toLowerCase()
          .replace(/[^a-z0-9]/g, "")
          .replace("onhold", "onHold")
          .replace("nonemirati", "nonEmirati")}`}
        style={{
          backgroundColor: selectedStyle.background,
          color: selectedStyle.color,
        }}
        onClick={() => setIsOpen((prevState) => !prevState)} // Toggling the state
      >
        <span>{changedVal}</span>
        <img
          src={DropdownIcon}
          alt="dropdown"
          className={`status-dropdown__arrow ${isOpen ? "rotated" : ""}`}
          style={{
            width: "14px",
            height: "9px",
            filter: `brightness(0) saturate(100%) invert(${
              selectedStyle.color === "#ffffff" ? "100%" : "0%"
            })`,
          }}
        />
      </div>

      {isOpen && (
        <div className="status-dropdown__options">
          {(
            statusOptions ||
            Object.keys(statusStyles).map((key) => ({
              key,
              label: key,
              order: 0,
            }))
          ).map((option) => {
            const isSelected = changedVal === option.label;

            return (
              <MenuItem
                selected={isSelected}
                key={option.key}
                onClick={() => handleSelectChange(option.label)}
                className="status-dropdown__menu-item"
              >
                <div
                  className={`status-dropdown__option ${
                    isSelected ? "selected" : ""
                  }`}
                >
                  {option.label}
                </div>
              </MenuItem>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StatusDropDown;
